import type { CryptoCurrency } from '@0xtorch/core'
import { cardanoAssetMetadataSchema, cardanoNativeUnitSchema } from './schemas'

/** ADA の既存価格データソース ID。 */
export const cardanoAda: CryptoCurrency = {
  type: 'CryptoCurrency',
  id: 'cardano',
  name: 'Cardano',
  symbol: 'ADA',
  icon: '/images/cardano.svg',
  market: { coingeckoId: 'cardano' },
  updatedAt: 0,
}

/**
 * Native Unit を CoinGecko の正規通貨へ解決し、未対応の場合は独自通貨を維持する。
 * @param params Native Unit、取得済み metadata、同期済みの対応情報と通貨一覧
 * @returns 数量変換に用いる decimals と通貨情報
 */
export const createCardanoNativeAsset = (params: {
  unit: string
  metadata?: unknown
  existing?: CryptoCurrency
  currencies?: readonly CryptoCurrency[]
}): { asset: CryptoCurrency; decimals: number } => {
  const unit = cardanoNativeUnitSchema.parse(params.unit)
  const id = `cardano:${unit}`
  const metadataResult = cardanoAssetMetadataSchema.safeParse(params.metadata)
  const metadata =
    metadataResult.success && metadataResult.data.asset === unit
      ? metadataResult.data.metadata
      : undefined
  const fallback = `Cardano ${unit.slice(0, 8)}.${unit.slice(56) || 'empty'}`
  let decoded: string | undefined
  try {
    const bytes = Uint8Array.from(unit.slice(56).match(/.{2}/g) ?? [], (byte) =>
      Number.parseInt(byte, 16),
    )
    decoded = new TextDecoder('utf-8', {
      fatal: true,
      ignoreBOM: false,
    }).decode(bytes)
  } catch {
    decoded = undefined
  }
  const name =
    safeText({ text: metadata?.name }) ??
    safeText({ text: decoded }) ??
    fallback
  const symbol =
    safeText({ text: metadata?.ticker }) ??
    safeText({ text: decoded }) ??
    fallback
  const related = params.existing?.id === id ? params.existing : undefined
  const coinId = related?.market?.coingeckoId
  const coin = coinId
    ? params.currencies?.find(
        (currency) =>
          currency.id === coinId && currency.market?.coingeckoId === coinId,
      )
    : undefined
  return {
    asset:
      coin ??
      (params.existing?.id === id
        ? params.existing
        : { type: 'CryptoCurrency', id, name, symbol, updatedAt: 0 }),
    decimals: metadata?.decimals ?? 0,
  }
}

/**
 * 非印字文字や制御文字を資産表示名へ持ち込まない。
 * @param params.text metadata または UTF-8 Asset Name
 * @returns 安全な表示文字列。不明な場合は undefined
 */
const safeText = (params: { text?: string | null }): string | undefined =>
  params.text && params.text.trim().length > 0 && !/\p{C}/u.test(params.text)
    ? params.text
    : undefined
